import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Bot, Check, Loader2, X } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import { inputClass, PanelFrame, primaryButtonClass, secondaryButtonClass, ServerError } from "@/components/Panel";
import { Badge, type StatusTone } from "@/components/ui";
import {
  acceptSuggestion,
  agentErrorMessage,
  agentKeys,
  rejectSuggestion,
  suggestionListQuery,
  type SuggestionRow,
  type SuggestionStatus,
} from "@/features/agents/api";
import { inboxStatuses, validateInboxSearch, type InboxSearch } from "@/features/agents/search";
import { suggestionText } from "@/features/agents/text";
import { purchaseOrderKeys } from "@/features/purchase-orders/api";
import { cn } from "@/lib/cn";
import { useFormat } from "@/lib/format";

// Planning → Agent suggestions (PX-PLN-130, docs/design/agents.md): what the
// agents propose, newest first per status. A person with the action's
// permission accepts (the RPC runs as that person) or rejects with a note.
// One RPC per page (keyset, 50), one per decision.

type Search = InboxSearch;

export const Route = createFileRoute("/_app/planning/suggestions")({
  validateSearch: validateInboxSearch,
  component: SuggestionsPage,
});

const statusTone: Record<SuggestionStatus, StatusTone> = {
  proposed: "info",
  executed: "success",
  rejected: "neutral",
  expired: "warning",
  superseded: "neutral",
};

function SuggestionsPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const query = useInfiniteQuery(suggestionListQuery(search.status));
  const rows = query.data?.pages.flatMap((p) => p.rows) ?? [];
  const selected = rows.find((r) => r.id === search.id);

  const columns: Column<SuggestionRow>[] = [
    { key: "time", header: t("agents.colTime"), width: "minmax(130px,0.8fr)", className: "tabular text-ink-muted", cell: (r) => fmt.dateTime(r.created_at) },
    {
      key: "title",
      header: t("agents.colTitle"),
      width: "minmax(240px,2.4fr)",
      cell: (r, sel) => (
        <span className={cn("truncate font-medium", sel && "text-accent-ink")}>{suggestionText(t, r, fmt.day, (v) => fmt.qty(String(v))).title}</span>
      ),
    },
    { key: "agent", header: t("agents.colAgent"), width: "minmax(110px,0.8fr)", cell: (r) => t(`agents.code.${r.agent_code}`) },
    { key: "company", header: t("agents.colCompany"), width: "minmax(70px,0.5fr)", className: "font-mono text-xs", cell: (r) => r.company_code },
    { key: "status", header: t("agents.colStatus"), width: "minmax(100px,0.7fr)", cell: (r) => <Badge tone={statusTone[r.status]}>{t(`agents.status.${r.status}`)}</Badge> },
  ];

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-col gap-0.5">
        <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
          {t("nav.planning.title")} <span aria-hidden="true">/</span> {t("nav.planning.suggestions")}
        </nav>
        <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("agents.inboxTitle")}</h1>
        <p className="text-[13px] text-ink-muted">{t("agents.inboxHint")}</p>
      </header>

      <div role="group" aria-label={t("agents.colStatus")} className="flex flex-wrap gap-1.5">
        {inboxStatuses.map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={search.status === s}
            onClick={() => setSearch({ status: s, id: undefined })}
            className={cn(
              "h-8 rounded-lg border px-3 text-[13px] font-medium",
              search.status === s ? "border-accent bg-accent-soft text-accent-ink" : "border-border hover:bg-surface-hover",
            )}
          >
            {t(`agents.status.${s}`)}
          </button>
        ))}
      </div>

      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("agents.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={search.id}
          onSelect={(r) => setSearch({ id: r.id })}
          onClear={() => setSearch({ id: undefined })}
          emptyTitle={t("agents.emptyTitle")}
          emptyBody={t(search.status === "proposed" ? "agents.emptyBodyProposed" : "agents.emptyBody")}
          emptyAction={
            <Link to="/settings/agents" className={secondaryButtonClass}>
              <Bot size={16} aria-hidden="true" />
              {t("agents.manage")}
            </Link>
          }
          minWidth={720}
        />
        <DetailPanel
          label={t("agents.detailLabel")}
          open={!!search.id}
          placeholder={<DetailPlaceholder title={t("agents.selectPrompt")} hint={t("agents.selectPromptHint")} />}
        >
          {selected && (
            <SuggestionPanel
              key={selected.id}
              row={selected}
              onClose={() => setSearch({ id: undefined })}
              // A decided suggestion leaves the open list: follow it to its new status.
              onDecided={(status) => setSearch({ status, id: selected.id })}
            />
          )}
        </DetailPanel>
      </div>
    </div>
  );
}

interface CreatedDoc {
  id: string;
  number: string;
}

/** convert_planned_orders → { purchase_orders: [{ id, number }], production_orders: [...] }. */
function createdDocs(result: unknown): { purchase: CreatedDoc[]; production: CreatedDoc[] } {
  const r = (result ?? {}) as { purchase_orders?: CreatedDoc[]; production_orders?: CreatedDoc[] };
  return { purchase: r.purchase_orders ?? [], production: r.production_orders ?? [] };
}

function SuggestionPanel({
  row,
  onClose,
  onDecided,
}: {
  row: SuggestionRow;
  onClose: () => void;
  onDecided: (status: SuggestionStatus) => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: agentKeys.all }),
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all }),
      queryClient.invalidateQueries({ queryKey: ["planning"] }),
    ]);
  const accept = useMutation({
    mutationFn: () => acceptSuggestion(row.id),
    onMutate: () => setError(null),
    onSuccess: async () => {
      await refresh();
      onDecided("executed");
    },
    onError: (e) => setError(agentErrorMessage(t, e)),
  });
  const reject = useMutation({
    mutationFn: () => rejectSuggestion(row.id, note.trim() || null),
    onMutate: () => setError(null),
    onSuccess: async () => {
      await refresh();
      onDecided("rejected");
    },
    onError: (e) => setError(agentErrorMessage(t, e)),
  });
  const ids = Array.isArray(row.args.p_ids) ? row.args.p_ids.length : 0;
  const docs = createdDocs(row.result);
  const text = suggestionText(t, row, fmt.day, (v) => fmt.qty(String(v)));
  const busy = accept.isPending || reject.isPending;

  return (
    <PanelFrame
      eyebrow={`${t(`agents.code.${row.agent_code}`)} · ${fmt.dateTime(row.created_at)}`}
      title={text.title}
      onClose={onClose}
      badge={<Badge tone={statusTone[row.status]}>{t(`agents.status.${row.status}`)}</Badge>}
    >
      <div className="flex flex-col gap-4 text-[13px]">
        <section className="flex flex-col gap-1">
          <h3 className="font-semibold">{t("agents.why")}</h3>
          <ul className="flex flex-col gap-0.5 text-ink-soft">
            {text.lines.map((l, i) => (
              <li key={i} className={cn(l.startsWith("·") && "tabular pl-3")}>
                {l}
              </li>
            ))}
          </ul>
        </section>
        <section className="flex flex-col gap-1">
          <h3 className="font-semibold">{t("agents.action")}</h3>
          <p>{t(`agents.kind.${row.kind}`, { count: ids })}</p>
          <p className="text-xs text-ink-muted">
            {t("agents.company", { code: row.company_code })} · {t("agents.expires", { date: fmt.dateTime(row.expires_at) })}
          </p>
        </section>
        {row.status === "executed" && (
          <section className="flex flex-col gap-1">
            <h3 className="font-semibold">{t("agents.outcome")}</h3>
            <p>
              {t("agents.executedBy", { name: row.decided_by_name ?? "—", date: row.decided_at ? fmt.dateTime(row.decided_at) : "—" })}
            </p>
            <ul className="flex flex-wrap gap-x-3 font-mono text-xs">
              {docs.purchase.map((d) => (
                <li key={d.id}>
                  <Link to="/procurement/purchase-orders" search={{ status: "all", q: "", po: d.id }} className="text-accent-ink hover:underline">
                    {d.number}
                  </Link>
                </li>
              ))}
              {docs.production.map((d) => (
                <li key={d.id}>
                  <Link to="/manufacturing/production-orders" search={{ status: "all", q: "", po: d.id }} className="text-accent-ink hover:underline">
                    {d.number}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
        {row.status === "rejected" && (
          <p className="text-ink-soft">
            {t("agents.rejectedBy", { name: row.decided_by_name ?? "—" })}
            {row.decision_note && ` — ${row.decision_note}`}
          </p>
        )}
        <ServerError message={error} />
        {row.status === "proposed" &&
          (row.can_decide ? (
            rejecting ? (
              <div className="flex flex-col gap-2">
                <label htmlFor="sg-note" className="font-medium">
                  {t("agents.rejectNote")}
                </label>
                <input id="sg-note" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} className={inputClass} />
                <div className="flex gap-2">
                  <button type="button" disabled={busy} onClick={() => reject.mutate()} className={secondaryButtonClass}>
                    {reject.isPending ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <X size={16} aria-hidden="true" />}
                    {t("agents.reject")}
                  </button>
                  <button type="button" onClick={() => setRejecting(false)} className="text-ink-muted hover:underline">
                    {t("form.cancel")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <button type="button" disabled={busy} onClick={() => accept.mutate()} className={primaryButtonClass}>
                  {accept.isPending ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Check size={16} aria-hidden="true" />}
                  {t(`agents.accept.${row.kind}`)}
                </button>
                <button type="button" disabled={busy} onClick={() => setRejecting(true)} className={secondaryButtonClass}>
                  {t("agents.reject")}
                </button>
              </div>
            )
          ) : (
            <p className="text-ink-muted">{t("agents.cannotDecide")}</p>
          ))}
      </div>
    </PanelFrame>
  );
}
